import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ArrowRight, Check, Lock, Clock, Loader2, DollarSign, XCircle } from 'lucide-react';
import { getPlatformMeta, getActionMeta, formatMoney } from '../data/taskMeta';
import taskService from '../services/taskService';
import { useTranslation } from '../i18n/LocaleContext';
import API_CONFIG from '../config/api';

// UX Pilot「Learn」模板:白底、靛蓝主色、进度卡 + 课程式步骤时间线
const TaskDetailSheet = ({ task, onClose, onChanged, onError }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);

  const { label, Icon: PlatformIcon, tint: platformTint } = getPlatformMeta(task.platform);
  const { verbKey, img: ActionImg } = getActionMeta(task.actionType);

  const openTask = task.openTask;
  const status = openTask ? openTask.status : 'idle';

  useEffect(() => {
    if (!openTask || openTask.status !== 'claimed' || !openTask.expireAt) {
      setRemaining(null);
      return undefined;
    }
    const tick = () => {
      const ms = new Date(openTask.expireAt).getTime() - Date.now();
      setRemaining(ms > 0 ? ms : 0);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [openTask]);

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      if (err.errorCode === 'KYC_REQUIRED') {
        onError(t('kycRequiredToOrder'));
        onClose();
        navigate('/vi');
      } else if (err.errorCode === 'LUCKY_ORDER_PENDING') {
        // 有待支付订单时引导去订单页支付，措辞不暴露隐藏剧本
        onError(t('ordLuckyFirst'));
        onClose();
        navigate('/don-hang');
      } else if (err.errorCode === 'SOCIAL_REQUIRED') {
        // 未绑定该平台账号：带去钱包页自动打开绑定弹层
        onError(t('socialRequiredToOrder').replace('{platform}', getPlatformMeta(err.platform || task.platform).label));
        onClose();
        navigate('/vi?social=1');
      } else if (err.errorCode === 'SOCIAL_PENDING') {
        onError(t('socialPendingToOrder').replace('{platform}', getPlatformMeta(err.platform || task.platform).label));
      } else {
        onError(err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const formatCountdown = (ms) => {
    const total = Math.floor(ms / 1000);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const steps = [
    t('taskStepOne').replace('{verb}', t(verbKey)),
    t('taskStepTwo')
  ];

  // 步骤状态映射任务状态:done 绿勾 / current 靛蓝高亮 / locked 灰锁 / failed 红叉
  const stepState = (i) => {
    if (status === 'claimed' || status === 'pending_review') return i === 0 ? 'done' : 'current';
    if (status === 'idle' && task.finalStatus === 'approved') return 'done';
    if (status === 'idle' && task.finalStatus === 'rejected') return i < 1 ? 'done' : 'failed';
    return i === 0 ? 'current' : 'locked';
  };
  const doneCount = [0, 1].filter((i) => stepState(i) === 'done').length;

  let cta = null;
  if (status === 'idle' && !task.finalStatus) {
    cta = { label: task.available ? t('taskAccept') : t('taskNotAvailable'), onClick: () => run(() => taskService.claim(task.id)), disabled: busy || !task.available };
  } else if (status === 'claimed') {
    cta = { label: `${t('taskOpenPlatform')} · ${label}`, onClick: () => window.open(task.targetUrl, '_blank', 'noopener') };
  } else if (status === 'pending_review') {
    cta = { label: t('taskAwaitingReview'), disabled: true };
  } else if (task.finalStatus === 'approved') {
    cta = { label: t('taskDone'), disabled: true };
  } else if (task.finalStatus === 'rejected') {
    cta = { label: t('taskRejected'), disabled: true };
  }

  const stepSubtitle = (i) => {
    const st = stepState(i);
    if (st === 'done') return t('taskStepDoneLbl');
    if (st === 'failed') return task.rejectReason || t('taskRejected');
    if (st !== 'current') return t('taskStepLockedLbl');
    if (status === 'claimed' && i === 1 && remaining !== null) {
      return `${t('taskStepCurrentLbl')} · ${formatCountdown(remaining)}`;
    }
    return t('taskStepCurrentLbl');
  };

  return (
    <div className="fixed inset-0 z-[60] bg-white overflow-y-auto scrollbar-hide animate-fade-in">
      {/* 顶部:返回 + 平台徽章 + 标题 + 奖励胶囊 + 头像 */}
      <div className="sticky top-0 z-10 bg-white">
        <div className="max-w-md mx-auto flex items-center gap-2.5 px-4 h-16">
          <button
            type="button"
            onClick={onClose}
            aria-label={t('back')}
            className="w-9 h-9 -ml-1.5 shrink-0 flex items-center justify-center text-ink"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2.6} />
          </button>
          <span className={`w-11 h-11 shrink-0 rounded-full ${platformTint} flex items-center justify-center text-white`}>
            <PlatformIcon className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-bold text-ink leading-tight truncate">{label} · {t(verbKey)}</p>
            <p className="mt-0.5 text-[12.5px] text-gray-400 leading-none truncate">{task.title}</p>
          </div>
          <span className="shrink-0 flex items-center gap-1 rounded-full bg-amber-50 px-3 py-2 tabular text-[13px] font-bold text-ink">
            <DollarSign className="w-4 h-4 text-amber-500" />
            {formatMoney(task.reward)}
          </span>
          <span className="w-11 h-11 shrink-0 rounded-full overflow-hidden ring-1 ring-black/5">
            <img
              src={API_CONFIG.getImageUrl(task.imageUrl)}
              alt={label}
              className="w-full h-full object-cover"
            />
          </span>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pb-10">
        {/* 主信息卡:封面 + 标题 + 描述 + 进度条 + 主按钮 */}
        <div className="rounded-xl bg-[#F2F2FA] p-4">
          <div className="relative h-44 rounded-xl overflow-hidden bg-black">
            {task.videoUrl ? (
              <video
                key={task.videoUrl}
                src={API_CONFIG.getImageUrl(task.videoUrl)}
                poster={API_CONFIG.getImageUrl(task.imageUrl) || undefined}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                controls
              />
            ) : (
              <img
                src={API_CONFIG.getImageUrl(task.imageUrl)}
                alt={task.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <p className="absolute bottom-0 left-0 right-0 px-3.5 pb-2.5 flex items-center gap-2 text-[12.5px] font-medium text-white drop-shadow">
              {ActionImg ? <img src={ActionImg} alt="" className="w-5 h-5" /> : null}
              {label} · {t(verbKey)}
            </p>
          </div>
          <h2 className="mt-4 px-1 text-[23px] font-extrabold text-ink leading-tight break-words">{task.title}</h2>
          {task.description && (
            <p className="mt-2 px-1 text-[14px] text-gray-500 leading-relaxed">{task.description}</p>
          )}
          <div className="mt-4 px-1 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-white overflow-hidden">
              <div
                className="h-full rounded-full bg-blue-500 transition-all"
                style={{ width: `${(doneCount / steps.length) * 100}%` }}
              />
            </div>
            <span className="text-[13px] text-gray-500 tabular">{t('taskStepsDone', { done: doneCount, total: steps.length })}</span>
          </div>
          {cta && (
            <button
              type="button"
              onClick={cta.onClick}
              disabled={cta.disabled}
              className="mt-4 w-full h-[54px] flex items-center justify-center gap-2 rounded-xl bg-blue-500
                text-[15.5px] font-bold text-white disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {cta.label}
              {!cta.disabled && !busy ? <ArrowRight className="w-[18px] h-[18px]" /> : null}
            </button>
          )}
        </div>

        {/* 步骤时间线 */}
        <p className="mt-7 mb-4 px-1 text-[12px] font-bold uppercase tracking-wider text-gray-400">{t('taskHowTo')}</p>
        <div className="px-1">
          {steps.map((step, i) => {
            const st = stepState(i);
            return (
              <div key={i} className="flex gap-3.5">
                <div className="flex flex-col items-center">
                  {st === 'done' && (
                    <span className="w-11 h-11 rounded-full bg-emerald-50 flex items-center justify-center">
                      <Check className="w-5 h-5 text-emerald-600" strokeWidth={2.6} />
                    </span>
                  )}
                  {st === 'current' && (
                    <span className="w-11 h-11 rounded-full bg-blue-500 flex items-center justify-center text-white text-[15px] font-extrabold tabular">
                      {i + 1}
                    </span>
                  )}
                  {st === 'failed' && (
                    <span className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-red-500" />
                    </span>
                  )}
                  {st === 'locked' && (
                    <span className="w-11 h-11 rounded-full bg-gray-50 flex items-center justify-center">
                      <Lock className="w-[18px] h-[18px] text-gray-400" />
                    </span>
                  )}
                  {i < steps.length - 1 && <span className="w-px flex-1 bg-gray-200 my-1" />}
                </div>
                <div className={st === 'current' || st === 'failed'
                  ? `flex-1 mb-3 rounded-xl border px-4 py-3.5 ${st === 'failed' ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`
                  : 'flex-1 pb-6'}
                >
                  <p className="text-[15px] font-bold text-ink leading-snug">{step}</p>
                  <p className={`mt-1 text-[13px] tabular ${
                    st === 'current' ? 'text-blue-600 font-medium'
                      : st === 'failed' ? 'text-red-500'
                      : st === 'locked' ? 'text-gray-400'
                      : 'text-gray-500'
                  }`}>
                    {stepSubtitle(i)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* 进行中:倒计时 + 提交(无需上传凭证) */}
        {status === 'claimed' && (
          <div className="mt-2 rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-3">
            <div className="flex items-center justify-between rounded-xl bg-white px-3.5 py-2.5">
              <span className="flex items-center gap-2 text-[13px] font-semibold text-ink">
                <Clock className="w-4 h-4 text-blue-600" />
                {t('taskTimeLeft')}
              </span>
              <span className="tabular text-[15px] font-bold text-ink">
                {remaining === null ? '--:--' : formatCountdown(remaining)}
              </span>
            </div>

            <button
              type="button"
              onClick={() => run(() => taskService.submitProof(task.id, null))}
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-blue-500 py-3 text-[15px] font-bold text-white disabled:opacity-45"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {t('taskSubmitProof')}
              {!busy ? <ArrowRight className="w-[18px] h-[18px]" /> : null}
            </button>
          </div>
        )}

        {/* 待审核:自动审核提示 */}
        {status === 'pending_review' && (
          <div className="mt-2 rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-[13px] text-gray-500 leading-relaxed">{t('taskReviewNote')}</p>
          </div>
        )}

        {/* 已通过:奖励 */}
        {status === 'idle' && task.finalStatus === 'approved' && (
          <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3">
            <span className="w-11 h-11 shrink-0 rounded-full bg-white flex items-center justify-center">
              <Check className="w-5 h-5 text-emerald-600" strokeWidth={2.6} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold text-ink">{t('taskDone')}</p>
              {task.reviewedAt && <p className="text-[11.5px] text-gray-400 mt-0.5 tabular">{new Date(task.reviewedAt).toLocaleString()}</p>}
            </div>
            <span className="tabular text-[16px] font-extrabold text-emerald-600">+{formatMoney(task.reward)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDetailSheet;
